import {
  AlertProps,
  LinearProgress,
  linearProgressClasses,
  styled,
} from "@mui/material";
import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { snackBarConfigType } from "../classes/AlertConfig";
import MuiAlert from "@mui/material/Alert";
import { GlobalContext } from "../contexts/GlobalContext";
import Header from "./ui/Header";
import DaysScroll from "./ui/DaysScroll";
import QuranProgressCard from "./ui/QuranProgressCard";
import ProgressListView from "./ui/ProgressListView";
import UserProgress from "../classes/UserProgress";
import GroupProgressListView from "./ui/GroupProgressListView";
import { fetchUserProgressFromDB } from "../services/firebase";
import { QURAN_SURAH } from "../classes/UserProgress";
import { set } from "firebase/database";
import { VERSION_NUMBER } from "../version_info";
import TwitterIcon from "../assets/icons/twitter-solid";
import GithubIcon from "../assets/icons/github-icon";
import LinkedInIcon from "../assets/icons/linkedin-icon";

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const mergeIntervals = (intervals: Interval[]): Interval[] => {
  if (intervals.length === 0) return [];
  // sort intervals by start
  intervals.sort((a, b) => a.start - b.start);
  const merged: Interval[] = [intervals[0]];

  for (let i = 1; i < intervals.length; i++) {
    const last = merged[merged.length - 1];
    const current = intervals[i];
    // if current interval overlaps or is contiguous with last
    if (current.start <= last.end + 1) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push(current);
    }
  }
  return merged;
};

export const generateUserProgressStats = (userProgress: UserProgress[]) => {
  if (!userProgress.length) {
    return {
      progressString: "No progress yet",
      percentageValue: 0,
      totalCompletedAyahs: 0,
    };
  }

  // Map to store intervals per chapter (Surah number)
  const intervalsByChapter = new Map<number, Interval[]>();

  // Helper: add an interval for a given chapter
  const addInterval = (chapter: number, start: number, end: number) => {
    if (!intervalsByChapter.has(chapter)) {
      intervalsByChapter.set(chapter, []);
    }
    intervalsByChapter.get(chapter)?.push({ start, end });
  };

  userProgress.forEach(({ progressFrom, progressTo }) => {
    const fromChapter = progressFrom.chapter;
    const toChapter = progressTo.chapter;

    if (fromChapter === toChapter) {
      // Progress within the same Surah
      addInterval(fromChapter, progressFrom.verse, progressTo.verse);
    } else {
      // Spanning multiple Surahs
      // For first chapter: from progressFrom.verse to end of surah
      const fromSurah = QURAN_SURAH.find((s) => s.number === fromChapter);
      if (fromSurah) {
        addInterval(fromChapter, progressFrom.verse, fromSurah.ayahs);
      }
      // For any chapters in between: full surah
      for (let ch = fromChapter + 1; ch < toChapter; ch++) {
        const surah = QURAN_SURAH.find((s) => s.number === ch);
        if (surah) {
          addInterval(ch, 1, surah.ayahs);
        }
      }
      // For last chapter: from 1 to progressTo.verse
      addInterval(toChapter, 1, progressTo.verse);
    }
  });

  // Merge intervals per chapter and sum total completed ayahs
  let totalCompletedAyahs = 0;
  intervalsByChapter.forEach((intervals, chapter) => {
    const merged = mergeIntervals(intervals);
    merged.forEach(({ start, end }) => {
      totalCompletedAyahs += end - start + 1;
    });
  });

  // Calculate the total ayahs in the Quran
  const totalAyahsInQuran = QURAN_SURAH.reduce(
    (sum, surah) => sum + surah.ayahs,
    0
  );

  const percentageValue = Math.round(
    (totalCompletedAyahs / totalAyahsInQuran) * 100
  );

  // console.log("totalCompletedAyahs: ", totalCompletedAyahs);
  return {
    progressString: `${totalCompletedAyahs}/${totalAyahsInQuran} Ayahs completed`,
    percentageValue,
    totalCompletedAyahs,
  };
};

interface Interval {
  start: number;
  end: number;
}

const Dashboard = () => {
  const { logInWithGoogle, logOut, currentUser, getCurrentUser } =
    useContext(GlobalContext);

  const [snackBarConfig, setSnackBarConfig] = useState(snackBarConfigType);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);

  const [userProgressStats, setUserProgressStats] = useState<{
    progressString: string;
    percentageValue: number;
    totalCompletedAyahs: number;
  }>({ progressString: "", percentageValue: 0, totalCompletedAyahs: 0 });

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackBarConfig({ open: false, severity: "success", message: "" });
  };

  useEffect(() => {
    const currentUser = getCurrentUser();

    if (currentUser !== null) {
      // Fetch user progress from the database
      fetchUserProgressFromDB(currentUser.id)
        .then((userProgress) => {
          // generateUserProgressStats(userProgress);
          // console.log(userProgress);
          try {
            const userProgressArray = userProgress as UserProgress[];
            setUserProgress(userProgressArray);
            setUserProgressStats(generateUserProgressStats(userProgressArray));
          } catch (error) {
            console.error("Failed to process user progress: ", error);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch user progress: ", error);
        });

      setTimeout(() => {
        setSnackBarConfig({
          open: true,
          severity: "success",
          message: "Mashallah You've Come Back!",
        });
      }, 1000);
    }
    // console.log("Current User: ", getCurrentUser());
  }, [getCurrentUser]);

  // Usage
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDate());

  // User Progress
  const fetchUserProgressTest = (): UserProgress[] => {
    // Fetch user progress

    const quranProgressTest: UserProgress[] = [
      {
        id: "1",
        progressFrom: { book: "Al-Fatiha", chapter: 1, verse: 1 },
        progressTo: { book: "Al-Baqarah", chapter: 2, verse: 10 },
        status: 1,
        created_at: Date.now() - 864000000, // 10 days ago
        user_id: "user123",
      },
      {
        id: "2",
        progressFrom: { book: "Al-Baqarah", chapter: 2, verse: 11 },
        progressTo: { book: "Al-Baqarah", chapter: 2, verse: 50 },
        status: 1,
        created_at: Date.now() - 432000000, // 5 days ago
        user_id: "user123",
      },
      {
        id: "3",
        progressFrom: { book: "Al-Imran", chapter: 3, verse: 1 },
        progressTo: { book: "Al-Imran", chapter: 3, verse: 20 },
        status: 1,
        created_at: Date.now() - 172800000, // 2 days ago
        user_id: "user456",
      },
      {
        id: "4",
        progressFrom: { book: "An-Nisa", chapter: 4, verse: 100 },
        progressTo: { book: "An-Nisa", chapter: 4, verse: 150 },
        status: 1,
        created_at: Date.now() - 604800000, // 7 days ago
        user_id: "user789",
      },
      {
        id: "5",
        progressFrom: { book: "Al-Ma'idah", chapter: 5, verse: 1 },
        progressTo: { book: "Al-Ma'idah", chapter: 5, verse: 30 },
        status: 1,
        created_at: Date.now() - 259200000, // 3 days ago
        user_id: "user123",
      },
      {
        id: "6",
        progressFrom: { book: "Al-An'am", chapter: 6, verse: 50 },
        progressTo: { book: "Al-An'am", chapter: 6, verse: 100 },
        status: 1,
        created_at: Date.now() - 129600000, // 1.5 days ago
        user_id: "user456",
      },
      {
        id: "7",
        progressFrom: { book: "Al-A'raf", chapter: 7, verse: 1 },
        progressTo: { book: "Al-A'raf", chapter: 7, verse: 25 },
        status: 1,
        created_at: Date.now() - 86400000, // 1 day ago
        user_id: "user789",
      },
      {
        id: "8",
        progressFrom: { book: "Al-Anfal", chapter: 8, verse: 10 },
        progressTo: { book: "Al-Anfal", chapter: 8, verse: 40 },
        status: 1,
        created_at: Date.now() - 345600000, // 4 days ago
        user_id: "user123",
      },
      {
        id: "9",
        progressFrom: { book: "At-Tawbah", chapter: 9, verse: 1 },
        progressTo: { book: "At-Tawbah", chapter: 9, verse: 15 },
        status: 1,
        created_at: Date.now() - 691200000, // 8 days ago
        user_id: "user456",
      },
      {
        id: "10",
        progressFrom: { book: "Yunus", chapter: 10, verse: 20 },
        progressTo: { book: "Yunus", chapter: 10, verse: 50 },
        status: 1,
        created_at: Date.now() - 216000000, // 2.5 days ago
        user_id: "user789",
      },
    ];

    return quranProgressTest;
  };

  return (
    <>
      <Header />
      <div className="mx-4 my-4">
        <QuranProgressCard userProgress={userProgressStats} />
        <div className="mt-12"></div>
      </div>
      <DaysScroll selectedDay={selectedDay} setSelectedDay={setSelectedDay} />
      <div className="mx-4 my-4">
        <ProgressListView
          userProgress={userProgress}
          // persentageValue={20}
          persentageValue={userProgressStats.percentageValue}
          versesCompleted={userProgressStats.totalCompletedAyahs}
        />
        <GroupProgressListView userProgress={fetchUserProgressTest()} />
      </div>
      <div className="flex flex-col justify-center w-full items-center mt-12 mb-6">
        <p className="text-[#AFAEB3] text-3xl">{VERSION_NUMBER}</p>
        <p className="text-[#1b1b1b] text-xl">Developed with ❤️ in India</p>
        <a
          href="https://theriyazo.in"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1b1b1b8f] hover:underline transition-all duration-500"
        >
          RiyazAhamad (@theriyazo)
        </a>
        <div className="flex gap-4 mt-2">
          <a
            href="https://x.com/theriyazo"
            target="_blank"
            rel="noopener noreferrer"
          >
            <TwitterIcon color="#1b1b1b8f" size={20} />
          </a>
          <a
            href="https://github.com/theriyazo"
            target="_blank"
            rel="noopener noreferrer"
          >
            <GithubIcon color="#1b1b1b8f" size={20} />
          </a>
          <a
            href="https://linkedin.com/in/theriyazo"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon color="#1b1b1b8f" size={20} />
          </a>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
